import { prepareHair, recolorHair } from "./recolor";
export type HairAssets = {version: 1; width: number; height: number; base: string; mask: string; original?: string; sourceId: string; originalAligned: boolean};
export type HairRenderer = {width: number; height: number; mode: "worker" | "canvas"; render: (hex: string | null) => Promise<{pixels: Uint8ClampedArray; renderMs: number}>; dispose: () => void};

async function decode(url: string, width: number, height: number) {
  const img = new Image();
  img.src = url;
  await img.decode();
  if (img.naturalWidth !== width || img.naturalHeight !== height) throw new Error("Prepared image dimensions do not match");
  const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
  const context = canvas.getContext("2d", {willReadFrequently: true});
  if (!context) throw new Error("This browser cannot display photo previews");
  context.drawImage(img, 0, 0);
  return context.getImageData(0, 0, width, height).data;
}

export async function createHairRenderer(assets: HairAssets): Promise<HairRenderer> {
  if (assets.version !== 1 || assets.width * assets.height > 1600000 || !assets.base.startsWith("data:image/png;base64,") || !assets.mask.startsWith("data:image/png;base64,")) throw new Error("Invalid photo response");
  const [pixels, maskPixels] = await Promise.all([decode(assets.base, assets.width, assets.height), decode(assets.mask, assets.width, assets.height)]);
  const mask = new Uint8Array(assets.width * assets.height);
  for (let i = 0; i < mask.length; i++) mask[i] = maskPixels[i * 4];
  let worker: Worker | undefined;
  try { worker = new Worker(new URL("./worker.ts", import.meta.url)); } catch { /* Older browsers use the same canvas algorithm. */ }
  if (!worker) {
    const base = prepareHair(pixels, mask, assets.width, assets.height);
    let disposed = false;
    return {width: assets.width, height: assets.height, mode: "canvas", dispose: () => {disposed = true;}, render: async hex => {
      if (disposed) throw new Error("Preview closed");
      const start = performance.now(); return {pixels: recolorHair(base, hex), renderMs: performance.now() - start};
    }};
  }
  const activeWorker = worker;
  let sequence = 0, disposed = false;
  const pending = new Map<number, {resolve: (value: any) => void; reject: (reason: Error) => void; timer: ReturnType<typeof setTimeout>}>();
  const dispose = () => { disposed = true; activeWorker.terminate(); for (const item of pending.values()) {clearTimeout(item.timer); item.reject(new Error("Preview closed"));} pending.clear(); };
  activeWorker.onmessage = ({data}) => { const item = pending.get(data.id); if (!item) return; clearTimeout(item.timer); pending.delete(data.id); if (data.error) item.reject(new Error(data.error)); else item.resolve(data); };
  activeWorker.onerror = () => dispose();
  const send = (data: Record<string, unknown>, transfer: Transferable[] = []) => new Promise<any>((resolve, reject) => {
    if (disposed) { reject(new Error("Preview closed")); return; }
    const id = ++sequence;
    const timer = setTimeout(() => {pending.delete(id); reject(new Error("Preview took too long. Please retry."));}, 15000);
    pending.set(id, {resolve, reject, timer}); activeWorker.postMessage({...data, id}, transfer);
  });
  try { await send({type:"prepare", pixels, mask, width:assets.width, height:assets.height}, [pixels.buffer, mask.buffer]); }
  catch (error) { dispose(); throw error; }
  return {width:assets.width, height:assets.height, mode:"worker", dispose, render: hex => send({type:"render", hex})};
}

export function drawHair(canvas: HTMLCanvasElement, renderer: HairRenderer, pixels: Uint8ClampedArray) {
  canvas.width = renderer.width; canvas.height = renderer.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.putImageData(new ImageData(new Uint8ClampedArray(pixels), renderer.width, renderer.height), 0, 0);
}

export function canvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Image could not be saved")), "image/jpeg", .94));
}
